import * as React from 'react';
import { path, pathOr } from 'ramda';

import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { emailValidator, requiredValidator, validatorsPipe } from '../../utils/validators';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { useFormTracking } from '../../utils/useFormTracking';
import { FormInputsLayout } from '../../components/FormInputsLayout';

export const DEFAULT_EMAIL_PLACEHOLDER = 'Введите email';

export const EmailQuestionContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, placeholder, title, required },
    answersState,
    setNextStep,
    setAnswersState,
  } = props;

  const { trackSubmit, trackChange } = useFormTracking();

  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm({
    onSubmit(values) {
      setAnswersState(prevState => {
        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value: values[answerField],
          },
        };

        setNextStep(nextAnswersState);
        trackSubmit({
          isRequired: required,
          field: answerField,
          value: null,
        });

        return nextAnswersState;
      });
    },
    validators: {
      [answerField]: validatorsPipe([
        [requiredValidator(required), 'Введите корректный email'],
        [emailValidator, 'Введите корректный email'],
      ]),
    },
    initialValues: pickOnlyMeaning({
      [answerField]: path([answerField, 'value'], answersState),
    }),
    required,
  });

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<'input'>['onChange']>>(
    e => {
      trackChange();

      onChange(answerField, e.target.value);
    },
    [onChange, answerField, trackChange],
  );

  return (
    <QuestionLayoutContainer title={title} onSubmit={handleSubmit} disableSubmit={isSubmitDisabled}>
      <FormInputsLayout
        InputProps={{
          type: 'text',
          name: answerField,
          invalid: Boolean(errors[answerField]),
          errorMessage: errors[answerField],
          placeholder: placeholder || DEFAULT_EMAIL_PLACEHOLDER,
          onChange: handleChange,
          value: pathOr('', [answerField], values),
          autoCapitalize: 'none',
        }}
      />
    </QuestionLayoutContainer>
  );
};

EmailQuestionContainer.displayName = 'EmailQuestionContainer';
